import { useTranslation } from 'react-i18next'
import { getIn, useFormik } from 'formik'
import { ChangeEvent, MutableRefObject, useEffect, useState } from 'react'
import { defaultValues, getSortedNameTranslations, getValidationSchema } from 'components/forms/GroupForm/utils'
import { Translation } from 'types/Translation'
import { GroupFormFields } from 'components/forms/GroupForm/types'

import { Label } from 'types/Label'
import * as Styled from './GroupForm.styles'

interface GroupFormProps {
  isEdit?: boolean
  initialValues?: GroupFormFields
  onSave: (values: GroupFormFields) => void
  submitRef: MutableRefObject<() => void>
  initialLabels?: Label[]
}

const GroupForm: React.FC<GroupFormProps> = ({
  isEdit,
  initialValues,
  onSave: handleSave,
  submitRef,
}) => {
  const { t } = useTranslation()

  const [translationLanguages, setTranslationLanguages] = useState<string[]>(
    getSortedNameTranslations(initialValues?.nameTranslations)?.map(
      (value) => value.languageCode
    ) || []
  )

  useEffect(() => {
    setTranslationLanguages(
      getSortedNameTranslations(initialValues?.nameTranslations)?.map(
        (value) => value.languageCode
      ) || []
    )
  }, [initialValues])

  const submit = (values: GroupFormFields) => {
    handleSave(values)
  }

  const {
    values,
    handleChange,
    handleBlur,
    handleSubmit,
    touched,
    errors,
    setFieldValue,
    setFieldTouched,
    validateForm,
  } = useFormik<GroupFormFields>({
    initialValues: {
      ...defaultValues,
      ...initialValues,
      nameTranslations: getSortedNameTranslations(initialValues?.nameTranslations) || [],
    },
    validationSchema: getValidationSchema(!!isEdit),
    onSubmit: submit,
    enableReinitialize: true,
  })

  useEffect(() => {
    if (submitRef?.current) {
      submitRef.current = handleSubmit
    }
    return () => {
      if (submitRef?.current) {
        submitRef.current = () => {}
      }
    }
  }, [handleSubmit, submitRef])

  const getValueForLanguageCode = (translations: Translation[], languageCode: string) =>
    translations.find((translation) => translation.languageCode === languageCode)?.value

  const handleChangeTranslationLanguages = (event: ChangeEvent<{ value: unknown }>) => {
    const newTranslationLanguages = (event.target.value as string[]).sort()
    setTranslationLanguages(newTranslationLanguages)
    const currentNameTranslations = values.nameTranslations
    setFieldValue(
      'nameTranslations',
      newTranslationLanguages.map((lng) => ({
        languageCode: lng,
        value: getValueForLanguageCode(currentNameTranslations, lng) || '',
      }))
    )
  }

  const handleImageChange = (file: File) => {
    setFieldValue('image', file || null)
    setFieldTouched('image', true, false)
    validateForm({ ...values, image: file })
  }

  return (
    <Styled.Form>
      <Styled.InfoSection
        label={t('common.name')}
        editValueComponent={
          <Styled.TextField
            id='name'
            name='name'
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.name}
            errorMessage={t(errors.name || '')}
          />
        }
      />
      <Styled.InfoSection
        label={t('knowledgeBase.translations')}
        editValueComponent={
          <Styled.TranslationLanguagesSelect
            multiple
            value={translationLanguages}
            onChange={handleChangeTranslationLanguages}
          />
        }
      />
      {values.nameTranslations.map((value, index) => (
        <Styled.InfoSection
          key={value.languageCode}
          label={`${t('common.name')} - ${value.languageCode}`}
          editValueComponent={
            <Styled.TextField
              id={`nameTranslations[${index}].value`}
              name={`nameTranslations[${index}].value`}
              value={values.nameTranslations[index].value}
              onChange={handleChange}
              onBlur={handleBlur}
              touched={getIn(touched, `nameTranslations[${index}].value`)}
              errorMessage={t(getIn(errors, `nameTranslations[${index}].value`) || '')}
            />
          }
        />
      ))}
      <Styled.InfoSection
        label={t('knowledgeBase.photo')}
        editValueComponent={
          <Styled.UploadImageField
            disabled={!isEdit}
            error={!!errors.image}
            errorMessage={t(errors.image || '')}
            touched={touched.image}
            onFileChange={handleImageChange}
            file={values.image}
          />
        }
      />
      {/* <Styled.InfoSection
        label={t('common.labels')}
        editValueComponent={
          <Styled.LabelSelect
            id='labelIds'
            name='labelIds'
            value={values.labelIds}
            onChange={handleChange}
            onBlur={handleBlur}
            touched={touched.labelIds}
            multiple
            initialData={initialLabels}
          />
        }
      /> */}
    </Styled.Form>
  )
}

export { GroupForm }
